import {useCallback, useEffect, useState} from "react";
import {Alert, App as AntdApp, Button, Drawer, Input, Modal, Space, Typography} from "antd";
import {HistoryOutlined, ReloadOutlined} from "@ant-design/icons";

import {
	getCooperativaAiDescriptions,
	updateCooperativaAiDescription
} from "../api/orgApi.js";
import PromptHistoryTimeline from "@modules/prompt-history/components/PromptHistoryTimeline.jsx";

const {Text, Paragraph} = Typography;


export default function AiDescriptionModal({
	open,
	fieldKey,
	fieldLabel,
	cooperativaId,
	onClose,
	onSave
})
{
	const {message} = AntdApp.useApp();

	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [customDescription, setCustomDescription] = useState("");
	const [defaultDescription, setDefaultDescription] = useState("");
	const [isCustom, setIsCustom] = useState(false);
	const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
	const [historyKey, setHistoryKey] = useState(0);

	const loadDescription = useCallback(async () =>
	{
		if (!cooperativaId || !fieldKey)
			return;

		setLoading(true);
		try
		{
			const result = await getCooperativaAiDescriptions(cooperativaId);
			if (result.ok !== false)
			{
				const fieldData = (result.descriptions || []).find(
					d => d.fieldName.toLowerCase() === fieldKey.toLowerCase()
				);

				if (fieldData)
				{
					setDefaultDescription(fieldData.defaultDescription || fieldData.description || "");
					setCustomDescription(fieldData.isCustom ? fieldData.description : "");
					setIsCustom(fieldData.isCustom);
				}
				else
				{
					setDefaultDescription("");
					setCustomDescription("");
					setIsCustom(false);
				}
			}
			else
			{
				message.error(result.error || "Errore durante il caricamento");
				setDefaultDescription("");
				setCustomDescription("");
				setIsCustom(false);
			}
		}
		catch (error)
		{
			message.error("Errore durante il caricamento della descrizione");
			setDefaultDescription("");
			setCustomDescription("");
			setIsCustom(false);
		}
		finally
		{
			setLoading(false);
		}
	}, [cooperativaId, fieldKey, message]);

	useEffect(() =>
	{
		if (open && fieldKey && cooperativaId)
			loadDescription();
	}, [open, fieldKey, cooperativaId, loadDescription]);

	const handleSave = useCallback(async () =>
	{
		if (!cooperativaId || !fieldKey)
			return;

		setSaving(true);
		try
		{
			const result = await updateCooperativaAiDescription(
				cooperativaId,
				fieldKey,
				customDescription.trim()
			);

			if (result.ok !== false)
			{
				message.success(
					customDescription.trim()
						? "Descrizione personalizzata salvata"
						: "Descrizione ripristinata al valore di default"
				);
				setIsCustom(!!customDescription.trim());
				setHistoryKey(prev => prev + 1);
				onSave?.(fieldKey, !!customDescription.trim());
				onClose?.();
			}
			else
			{
				message.error(result.error || "Errore durante il salvataggio");
			}
		}
		catch (error)
		{
			message.error("Errore durante il salvataggio");
		}
		finally
		{
			setSaving(false);
		}
	}, [cooperativaId, fieldKey, customDescription, message, onSave, onClose]);

	const handleResetToDefault = useCallback(async () =>
	{
		setCustomDescription("");
		if (isCustom)
		{
			setSaving(true);
			try
			{
				const result = await updateCooperativaAiDescription(cooperativaId, fieldKey, "");
				if (result.ok !== false)
				{
					message.success("Descrizione ripristinata al valore di default");
					setIsCustom(false);
					onSave?.(fieldKey, false);
				}
				else
				{
					message.error(result.error || "Errore durante il ripristino");
				}
			}
			catch (error)
			{
				message.error("Errore durante il ripristino");
			}
			finally
			{
				setSaving(false);
			}
		}
	}, [cooperativaId, fieldKey, isCustom, message, onSave]);

	const handleRestore = useCallback((content) =>
	{
		setCustomDescription(content);
		setHistoryDrawerOpen(false);
		message.info("Versione ripristinata nell'editor");
	}, [message]);

	const handleCancel = useCallback(() =>
	{
		setCustomDescription("");
		setHistoryDrawerOpen(false);
		onClose?.();
	}, [onClose]);

	return (
		<Modal
			open={open}
			title={`Personalizza descrizione AI \u2014 ${fieldLabel || fieldKey}`}
			onCancel={handleCancel}
			width={640}
			footer={[
				<Button key="cancel" onClick={handleCancel} disabled={saving}>
					Annulla
				</Button>,
				<Button
					key="reset"
					icon={<ReloadOutlined/>}
					onClick={handleResetToDefault}
					disabled={saving || (!isCustom && !customDescription.trim())}
				>
					Ripristina default
				</Button>,
				<Button
					key="history"
					icon={<HistoryOutlined/>}
					onClick={() => setHistoryDrawerOpen(true)}
				>
					Cronologia
				</Button>,
				<Button
					key="save"
					type="primary"
					onClick={handleSave}
					loading={saving}
					disabled={loading}
				>
					Salva
				</Button>
			]}
		>
			<Space direction="vertical" size={16} style={{width: "100%", marginBottom: 16}}>
				<Alert
					type="info"
					showIcon
					message="Descrizione default"
					description={
						<Paragraph
							style={{marginBottom: 0, whiteSpace: "pre-wrap"}}
							ellipsis={{rows: 3, expandable: true, symbol: "mostra tutto"}}
						>
							{defaultDescription || "Nessuna descrizione default disponibile"}
						</Paragraph>
					}
				/>

				<div>
					<Text strong style={{display: "block", marginBottom: 8}}>
						Descrizione personalizzata
					</Text>
					<Text type="secondary" style={{display: "block", marginBottom: 8}}>
						Lascia vuoto per usare la descrizione di default
					</Text>
					<Input.TextArea
						value={customDescription}
						onChange={(e) => setCustomDescription(e.target.value)}
						placeholder={defaultDescription}
						autoSize={{minRows: 4, maxRows: 10}}
						maxLength={1000}
						showCount
						disabled={loading || saving}
					/>
				</div>

				{isCustom && (
					<Alert
						type="warning"
						showIcon
						message="Questa cooperativa ha una descrizione personalizzata per questo campo."
					/>
				)}
			</Space>

			<Drawer
				title={`Cronologia versioni \u2014 ${fieldLabel || fieldKey}`}
				width={480}
				open={historyDrawerOpen}
				onClose={() => setHistoryDrawerOpen(false)}
				destroyOnClose
			>
				<PromptHistoryTimeline
					key={historyKey}
					entityType="COOPERATIVA_AI_DESCRIPTION"
					entityId={cooperativaId && fieldKey ? `${cooperativaId}:${fieldKey}` : ""}
					onRestore={handleRestore}
					currentContent={customDescription}
				/>
			</Drawer>
		</Modal>
	);
}
